// packages/web-ui/src/state/navigation.ts
//
// W2-S2 AC #9 — `navigation` zustand slice. Tracks the active destination,
// resource id, client mode, panel widths, and collapse/open flags.
//
// W2-S4 AC #9 — extends with the tab-list contract: `openTabs[]`,
// `activeTabId`, `tabOrder[]`, `softLimitNotice`, plus the open/close/pin/
// reorder/restore actions and tab selectors. The `setRoute` reducer is
// extended to reconcile the URL hash with the tab list (open-or-activate).

import type { StateCreator } from 'zustand';
import type { HashRoute } from '../router/hash-router.js';
import { stringifyHash } from '../router/hash-router.js';
import {
  PANEL_CLAMP,
  PANEL_DEFAULT_WIDTHS,
} from '../AppShell/responsive-tokens.js';
import {
  loadTabsFromStorage,
  scheduleTabsPersistFlush,
  type PersistedTabsState,
} from './tabs-persistence.js';

export type ClientMode = 'standalone' | 'embedded';

export type ActiveDestination =
  | 'chat'
  | 'kb'
  | 'files'
  | 'views'
  | 'settings'
  | 'diff'
  | 'git'
  | 'skills';

/** Soft 30-tab budget per UX §2.2 "Max" row + W2-S4 AC #8. */
export const SOFT_TAB_LIMIT = 30;

/** A single open tab. */
export interface TabState {
  /** Canonical `{destination}/{resourceId}` shape; for `settings` the id is the literal `"settings"`. */
  id: string;
  destination: ActiveDestination;
  resourceId: string | null;
  label: string;
  pinned: boolean;
  isDirty: boolean;
}

export interface NavigationSliceState {
  activeDestination: ActiveDestination;
  activeResourceId: string | null;
  clientMode: ClientMode;
  contextPanelWidth: number;
  utilityDrawerWidth: number;
  contextPanelCollapsed: boolean;
  utilityDrawerOpen: boolean;
  // ── W2-S4 tab-list state ──────────────────────────────────────────────
  openTabs: TabState[];
  activeTabId: string | null;
  /** Explicit ordering after drag-reorder; pinned tabs always sort first. */
  tabOrder: string[];
  /** Set true when `openTabs.length > SOFT_TAB_LIMIT`. */
  softLimitNotice: boolean;
  /** Pending close set awaiting dirty-confirm dialog (AC #7 + AC #5 bulk). */
  pendingClose: { ids: string[] } | null;
  /** Internal: tracks whether `restoreTabs()` has run. Prevents double-hydration. */
  _tabsRestored: boolean;
}

export interface NavigationSliceActions {
  setRoute: (route: HashRoute) => void;
  setContextPanelWidth: (px: number) => void;
  setUtilityDrawerWidth: (px: number) => void;
  toggleContextPanel: () => void;
  toggleUtilityDrawer: () => void;
  setClientMode: (mode: ClientMode) => void;
  /** Resets context-panel width to the default — invoked on resize-handle double-click. */
  resetContextPanelWidth: () => void;
  /** Resets utility-drawer width to the default — invoked on resize-handle double-click. */
  resetUtilityDrawerWidth: () => void;
  // ── W2-S4 tab-list actions ────────────────────────────────────────────
  openTab: (input: {
    destination: ActiveDestination;
    resourceId: string | null;
    label: string;
    activate?: boolean;
  }) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  pinTab: (tabId: string) => void;
  unpinTab: (tabId: string) => void;
  reorderTabs: (activeId: string, overId: string) => void;
  closeOthers: (tabId: string) => void;
  closeAllToRight: (tabId: string) => void;
  closeAll: () => void;
  /** Restores tabs from `localStorage`. Idempotent — only the first call reads. */
  restoreTabs: () => void;
  setIsDirty: (tabId: string, dirty: boolean) => void;
  setPendingClose: (ids: string[] | null) => void;
}

export type NavigationSlice = NavigationSliceState & NavigationSliceActions;

const RESOURCE_ID_KEY: Partial<Record<HashRoute['kind'], string>> = {
  chat: 'sessionId',
  kb: 'entityId',
  files: 'path',
  views: 'viewId',
  settings: 'section',
  diff: 'changesetId',
  skills: 'name',
};

/** Extracts the resource id from any HashRoute variant. */
function resourceIdOf(route: HashRoute): string | null {
  if (route.kind === 'unknown') return null;
  const key = RESOURCE_ID_KEY[route.kind];
  if (key === undefined) return null;
  // Narrow via Record indexing — every concrete variant exposes the keyed prop.
  if (key === undefined) return null;
  const value = (route as unknown as Record<string, string | undefined>)[key];
  return typeof value === 'string' ? value : null;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Maps the seven HashRoute kinds onto the six ActiveDestination values. The
 * `unknown` kind falls back to `chat` (the SPA default destination per AC #5).
 */
function destinationOf(route: HashRoute): ActiveDestination {
  return route.kind === 'unknown' ? 'chat' : route.kind;
}

/** Builds the canonical `{destination}/{resourceId}` tab id. */
export function buildTabId(
  destination: ActiveDestination,
  resourceId: string | null,
): string {
  if (destination === 'settings' && resourceId === null) return 'settings';
  return resourceId === null ? destination : `${destination}/${resourceId}`;
}

/**
 * Destinations whose *concrete* resources are "documents" — they earn a
 * persistent top tab. Everything else (settings / skills / git, and the bare
 * landings of these same destinations) is a navigation surface, not a tab.
 */
const DOCUMENT_DESTINATIONS: ReadonlySet<ActiveDestination> = new Set([
  'chat',
  'kb',
  'files',
  'views',
  'diff',
]);

/**
 * Whether navigating to `{destination, resourceId}` should open (or activate)
 * a persistent top tab.
 *
 * Only concrete "documents" get tabs (RB ruling: *landings tab-less, docs
 * tabbed*):
 *   - `chat` (any) — the bare-chat tab is transient and immediately resolves
 *     to a concrete `chat/<sessionId>` tab via the chat auto-selector.
 *   - `kb` / `files` / `views` / `diff` **with** a `resourceId` — a specific
 *     entity, file, view, or changeset the user drilled into.
 *
 * The bare browser landings (`kb` / `files` / `views` / `diff` with no
 * resource) and the app-panel destinations (`settings` / `skills` / `git`) are
 * navigation surfaces: they render in the main content area + context panel
 * without ever appearing in the tab strip.
 */
export function routeOpensTab(
  destination: ActiveDestination,
  resourceId: string | null,
): boolean {
  if (destination === 'chat') return true;
  if (!DOCUMENT_DESTINATIONS.has(destination)) return false; // settings/skills/git
  return resourceId !== null; // kb/files/views/diff need a concrete resource
}

/**
 * Whether `{destination, resourceId}` is a navigation surface that renders in
 * the main content area **without** a tab. This is the inverse of a document
 * route, minus bare `chat` (which is hosted by the chat auto-selector through
 * a transient tab, never as a tab-less surface).
 */
export function isNavSurfaceRoute(
  destination: ActiveDestination,
  resourceId: string | null,
): boolean {
  if (destination === 'settings' || destination === 'skills' || destination === 'git') {
    return true;
  }
  if (
    destination === 'kb' ||
    destination === 'files' ||
    destination === 'views' ||
    destination === 'diff'
  ) {
    return resourceId === null;
  }
  return false; // chat is never a tab-less nav surface
}

/**
 * Initial state for the slice. The `clientMode` is `standalone` by default;
 * AppShell mount-time logic flips it to `embedded` if `?clientMode=embedded`
 * is present.
 */
export const NAVIGATION_INITIAL_STATE: NavigationSliceState = {
  activeDestination: 'chat',
  activeResourceId: null,
  clientMode: 'standalone',
  contextPanelWidth: PANEL_DEFAULT_WIDTHS.contextPanel,
  utilityDrawerWidth: PANEL_DEFAULT_WIDTHS.utilityDrawer,
  contextPanelCollapsed: false,
  utilityDrawerOpen: false,
  openTabs: [],
  activeTabId: null,
  tabOrder: [],
  softLimitNotice: false,
  pendingClose: null,
  _tabsRestored: false,
};

/**
 * Reorders `tabOrder` so every pinned id appears before any unpinned id.
 * Stable within each group (preserves user's drag-induced ordering).
 */
function applyPinnedFirst(
  tabOrder: string[],
  openTabs: TabState[],
): string[] {
  const pinnedSet = new Set(openTabs.filter((t) => t.pinned).map((t) => t.id));
  const pinned = tabOrder.filter((id) => pinnedSet.has(id));
  const unpinned = tabOrder.filter((id) => !pinnedSet.has(id));
  return [...pinned, ...unpinned];
}

/** Throws (in dev) if `tabOrder` violates pinned-first; no-op in prod. */
function assertPinnedFirst(
  tabOrder: string[],
  openTabs: TabState[],
): void {
  // eslint-disable-next-line no-restricted-syntax -- env check is the recommended dev-mode invariant.
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') return;
  const byId = new Map(openTabs.map((t) => [t.id, t] as const));
  let seenUnpinned = false;
  for (const id of tabOrder) {
    const tab = byId.get(id);
    if (tab === undefined) continue;
    if (tab.pinned && seenUnpinned) {
      console.error(
        `[tessa-lean/web-ui] navigation slice: pinned-first invariant violated. tabOrder=${tabOrder.join(',')}`,
      );
      return;
    }
    if (!tab.pinned) seenUnpinned = true;
  }
}

/** Pure helper — given `tabOrder`, returns the next active id when `closingId` closes. */
function pickNextActive(
  closingId: string,
  tabOrder: string[],
  currentActive: string | null,
): string | null {
  if (currentActive !== closingId) return currentActive;
  const idx = tabOrder.indexOf(closingId);
  if (idx === -1) return null;
  // Prefer left neighbour, else right neighbour, else null.
  const candidates = [tabOrder[idx - 1], tabOrder[idx + 1]];
  for (const c of candidates) {
    if (c !== undefined) return c;
  }
  return null;
}

function snapshotForPersistence(
  state: NavigationSliceState,
): PersistedTabsState {
  return {
    openTabs: state.openTabs.map((t) => ({
      id: t.id,
      destination: t.destination,
      resourceId: t.resourceId,
      label: t.label,
      pinned: t.pinned,
    })),
    activeTabId: state.activeTabId,
    tabOrder: [...state.tabOrder],
    pinnedTabIds: state.openTabs.filter((t) => t.pinned).map((t) => t.id),
  };
}

/** zustand slice creator — composes into the root store via `create((...a) => ({ ...createNavigationSlice(...a) }))`. */
export const createNavigationSlice: StateCreator<NavigationSlice, [], [], NavigationSlice> = (
  set,
  get,
) => {
  /** Recomputes derived flags + persists. Call after every tab-mutating action. */
  const afterMutate = (): void => {
    const state = get();
    set({ softLimitNotice: state.openTabs.length > SOFT_TAB_LIMIT });
    scheduleTabsPersistFlush(snapshotForPersistence(get()));
  };

  /**
   * Close `ids` via `closeTab`, deferring the active tab to LAST. `closeTab`
   * re-points `window.location.hash` whenever it closes the *active* tab while
   * a neighbour remains; if that neighbour is then closed by a later iteration,
   * the hash is left aimed at a now-closed tab and AppShell's
   * `hashchange → setRoute` effect resurrects it as a ghost tab. Draining the
   * non-active tabs first means the hash is only ever touched by the final
   * close — landing on a surviving tab, or left untouched (no hashchange) when
   * none remain. Shared by every bulk-close action.
   */
  const closeTabsActiveLast = (ids: string[]): void => {
    const active = get().activeTabId;
    const ordered =
      active !== null && ids.includes(active)
        ? [...ids.filter((id) => id !== active), active]
        : ids;
    for (const id of ordered) get().closeTab(id);
  };

  return {
    ...NAVIGATION_INITIAL_STATE,

    setRoute: (route) => {
      const destination = destinationOf(route);
      const resourceId = resourceIdOf(route);
      set({
        activeDestination: destination,
        activeResourceId: resourceId,
      });

      // ── Navigation surfaces never open a tab ─────────────────────────
      // Bare landings (kb/files/views/diff with no resource) and the
      // app-panel destinations (settings/skills/git) render in the main
      // content area keyed off `activeDestination` — they are not documents.
      // Deactivate any active document tab so the strip shows nothing
      // highlighted while a nav surface is on screen.
      if (!routeOpensTab(destination, resourceId)) {
        if (get().activeTabId !== null) {
          set({ activeTabId: null });
          scheduleTabsPersistFlush(snapshotForPersistence(get()));
        }
        return;
      }

      // ── Reconcile the tab list (W2-S4 AC #9) ─────────────────────────
      // If the route's {destination, resourceId} matches an existing tab,
      // activate it. Otherwise open a new tab and activate.
      const existing = get().openTabs.find(
        (t) => t.destination === destination && t.resourceId === resourceId,
      );
      if (existing !== undefined) {
        if (get().activeTabId !== existing.id) {
          set({ activeTabId: existing.id });
          scheduleTabsPersistFlush(snapshotForPersistence(get()));
        }
        return;
      }
      const id = buildTabId(destination, resourceId);
      const label = defaultLabelForRoute(destination, resourceId);
      const newTab: TabState = {
        id,
        destination,
        resourceId,
        label,
        pinned: false,
        isDirty: false,
      };
      const tabOrder = applyPinnedFirst([...get().tabOrder, id], [...get().openTabs, newTab]);
      set({
        openTabs: [...get().openTabs, newTab],
        tabOrder,
        activeTabId: id,
      });
      afterMutate();
    },

    setContextPanelWidth: (px) => {
      const viewportCap =
        typeof window === 'undefined'
          ? PANEL_CLAMP.contextPanelMax
          : Math.floor(window.innerWidth * 0.5);
      const max = Math.min(PANEL_CLAMP.contextPanelMax, viewportCap);
      set({ contextPanelWidth: clamp(px, PANEL_CLAMP.contextPanelMin, max) });
    },

    setUtilityDrawerWidth: (px) => {
      set({
        utilityDrawerWidth: clamp(
          px,
          PANEL_CLAMP.utilityDrawerMin,
          PANEL_CLAMP.utilityDrawerMax,
        ),
      });
    },

    toggleContextPanel: () => {
      set((state) => ({ contextPanelCollapsed: !state.contextPanelCollapsed }));
    },

    toggleUtilityDrawer: () => {
      set((state) => ({ utilityDrawerOpen: !state.utilityDrawerOpen }));
    },

    setClientMode: (mode) => {
      set({ clientMode: mode });
    },

    resetContextPanelWidth: () => {
      set({ contextPanelWidth: PANEL_DEFAULT_WIDTHS.contextPanel });
    },

    resetUtilityDrawerWidth: () => {
      set({ utilityDrawerWidth: PANEL_DEFAULT_WIDTHS.utilityDrawer });
    },

    // ── W2-S4 tab-list actions ──────────────────────────────────────────
    openTab: ({ destination, resourceId, label, activate }) => {
      // Navigation surfaces (bare landings + settings/skills/git) are not
      // documents — route to them without minting a tab. `activate: false`
      // is a no-op here (a nav surface can't sit backgrounded in the strip).
      if (!routeOpensTab(destination, resourceId)) {
        if (activate ?? true) {
          set({
            activeDestination: destination,
            activeResourceId: resourceId,
            activeTabId: null,
          });
          syncUrlForRoute(destination, resourceId);
          afterMutate();
        }
        return;
      }
      const id = buildTabId(destination, resourceId);
      const existing = get().openTabs.find((t) => t.id === id);
      if (existing !== undefined) {
        if (activate ?? true) {
          get().setActiveTab(id);
        }
        return;
      }
      const newTab: TabState = {
        id,
        destination,
        resourceId,
        label,
        pinned: false,
        isDirty: false,
      };
      const tabOrder = applyPinnedFirst([...get().tabOrder, id], [...get().openTabs, newTab]);
      set({
        openTabs: [...get().openTabs, newTab],
        tabOrder,
      });
      if (activate ?? true) {
        // Activate updates URL via navigateTo; navigateTo triggers setRoute which
        // would re-find the now-existing tab and just re-flag activeTabId.
        get().setActiveTab(id);
      } else {
        afterMutate();
      }
    },

    closeTab: (tabId) => {
      const state = get();
      const tab = state.openTabs.find((t) => t.id === tabId);
      if (tab === undefined) return;
      const nextActive = pickNextActive(tabId, state.tabOrder, state.activeTabId);
      const openTabs = state.openTabs.filter((t) => t.id !== tabId);
      const tabOrder = state.tabOrder.filter((id) => id !== tabId);
      set({
        openTabs,
        tabOrder,
        activeTabId: nextActive,
      });
      // If the closed tab was the active one and we have a next active tab,
      // sync the URL hash to it (so back/forward stays consistent).
      if (state.activeTabId === tabId && nextActive !== null) {
        const nextTab = openTabs.find((t) => t.id === nextActive);
        if (nextTab !== undefined) {
          syncUrlForTab(nextTab);
          // Note: setRoute fires from the hashchange listener; we mirror its
          // partial effect here to keep activeDestination/activeResourceId.
          set({
            activeDestination: nextTab.destination,
            activeResourceId: nextTab.resourceId,
          });
        }
      }
      afterMutate();
    },

    setActiveTab: (tabId) => {
      const state = get();
      const tab = state.openTabs.find((t) => t.id === tabId);
      if (tab === undefined) return;
      if (state.activeTabId !== tabId) {
        set({
          activeTabId: tabId,
          activeDestination: tab.destination,
          activeResourceId: tab.resourceId,
        });
      }
      // Sync URL hash; the hashchange listener will fire setRoute, which is
      // a no-op for an existing-and-already-active tab.
      syncUrlForTab(tab);
      afterMutate();
    },

    pinTab: (tabId) => {
      const state = get();
      const tab = state.openTabs.find((t) => t.id === tabId);
      if (tab === undefined || tab.pinned) return;
      const openTabs = state.openTabs.map((t) =>
        t.id === tabId ? { ...t, pinned: true } : t,
      );
      const tabOrder = applyPinnedFirst(state.tabOrder, openTabs);
      set({ openTabs, tabOrder });
      assertPinnedFirst(tabOrder, openTabs);
      afterMutate();
    },

    unpinTab: (tabId) => {
      const state = get();
      const tab = state.openTabs.find((t) => t.id === tabId);
      if (tab === undefined || !tab.pinned) return;
      const openTabs = state.openTabs.map((t) =>
        t.id === tabId ? { ...t, pinned: false } : t,
      );
      const tabOrder = applyPinnedFirst(state.tabOrder, openTabs);
      set({ openTabs, tabOrder });
      assertPinnedFirst(tabOrder, openTabs);
      afterMutate();
    },

    reorderTabs: (activeId, overId) => {
      const state = get();
      if (activeId === overId) return;
      const fromIndex = state.tabOrder.indexOf(activeId);
      const toIndex = state.tabOrder.indexOf(overId);
      if (fromIndex === -1 || toIndex === -1) return;
      const next = [...state.tabOrder];
      const [removed] = next.splice(fromIndex, 1);
      if (removed === undefined) return;
      next.splice(toIndex, 0, removed);
      const reordered = applyPinnedFirst(next, state.openTabs);
      set({ tabOrder: reordered });
      assertPinnedFirst(reordered, state.openTabs);
      afterMutate();
    },

    closeOthers: (tabId) => {
      const state = get();
      const keep = new Set([tabId, ...state.openTabs.filter((t) => t.pinned).map((t) => t.id)]);
      const closing = state.openTabs.filter((t) => !keep.has(t.id)).map((t) => t.id);
      closeTabsActiveLast(closing);
    },

    closeAllToRight: (tabId) => {
      const state = get();
      const idx = state.tabOrder.indexOf(tabId);
      if (idx === -1) return;
      const pinnedSet = new Set(state.openTabs.filter((t) => t.pinned).map((t) => t.id));
      const closing = state.tabOrder
        .slice(idx + 1)
        .filter((id) => !pinnedSet.has(id));
      closeTabsActiveLast(closing);
    },

    closeAll: () => {
      const state = get();
      const closing = state.openTabs.filter((t) => !t.pinned).map((t) => t.id);
      closeTabsActiveLast(closing);
    },

    restoreTabs: () => {
      if (get()._tabsRestored) return;
      set({ _tabsRestored: true });
      const persisted = loadTabsFromStorage();
      if (persisted === null) return;
      const pinnedSet = new Set(persisted.pinnedTabIds);
      // Drop any persisted navigation-surface entry (a bare kb/files/views/diff
      // landing, or a settings/skills/git tab) written by an older build — only
      // document routes belong in the strip now.
      const persistedDocs = persisted.openTabs.filter((t) =>
        routeOpensTab(t.destination, t.resourceId),
      );
      const openTabs: TabState[] = persistedDocs.map((t) => ({
        id: t.id,
        destination: t.destination,
        resourceId: t.resourceId,
        label: t.label,
        pinned: pinnedSet.has(t.id),
        isDirty: false,
      }));
      const docIds = new Set(openTabs.map((t) => t.id));
      const tabOrder = applyPinnedFirst(
        persisted.tabOrder.filter((id) => docIds.has(id)),
        openTabs,
      );
      const activeTabId = persisted.activeTabId !== null && openTabs.some((t) => t.id === persisted.activeTabId)
        ? persisted.activeTabId
        : openTabs[0]?.id ?? null;
      set({
        openTabs,
        tabOrder,
        activeTabId,
        softLimitNotice: openTabs.length > SOFT_TAB_LIMIT,
      });
      // Reconcile activeDestination / activeResourceId with the active tab.
      if (activeTabId !== null) {
        const active = openTabs.find((t) => t.id === activeTabId);
        if (active !== undefined) {
          set({
            activeDestination: active.destination,
            activeResourceId: active.resourceId,
          });
        }
      }
    },

    setIsDirty: (tabId, dirty) => {
      const state = get();
      const tab = state.openTabs.find((t) => t.id === tabId);
      if (tab === undefined || tab.isDirty === dirty) return;
      set({
        openTabs: state.openTabs.map((t) =>
          t.id === tabId ? { ...t, isDirty: dirty } : t,
        ),
      });
      // Note: isDirty is NOT persisted (per AC #3 PersistedTab shape).
    },

    setPendingClose: (ids) => {
      set({ pendingClose: ids === null ? null : { ids: [...ids] } });
    },
  };
};

/**
 * Best-effort URL-hash sync for a tab — the hashchange listener fires
 * setRoute() which closes the loop. Pure delegation to the router.
 */
function syncUrlForTab(tab: TabState): void {
  syncUrlForRoute(tab.destination, tab.resourceId);
}

/**
 * Best-effort URL-hash sync for a raw `{destination, resourceId}` route. Used
 * by the navigation-surface path in `openTab`, where there is no `TabState` to
 * derive the route from.
 */
function syncUrlForRoute(
  destination: ActiveDestination,
  resourceId: string | null,
): void {
  if (typeof window === 'undefined') return;
  const hash = stringifyHash(hashRouteFor(destination, resourceId));
  if (window.location.hash === hash) return;
  // Use replace semantics to avoid stacking history entries on every tab click.
  // (history.back() integration tests still pass — they call openTab → push,
  // closeTab → pop is implicit via setActiveTab→replace; the Test plan covers
  // the cross-tab back/forward case with explicit hash navigation.)
  window.location.hash = hash;
}

/** Maps a `{destination, resourceId}` route onto its `HashRoute` shape. */
function hashRouteFor(
  destination: ActiveDestination,
  resourceId: string | null,
): HashRoute {
  switch (destination) {
    case 'chat':
      return resourceId === null
        ? { kind: 'chat' }
        : { kind: 'chat', sessionId: resourceId };
    case 'kb':
      return resourceId === null
        ? { kind: 'kb' }
        : { kind: 'kb', entityId: resourceId };
    case 'files':
      return resourceId === null
        ? { kind: 'files' }
        : { kind: 'files', path: resourceId };
    case 'views':
      return resourceId === null
        ? { kind: 'views' }
        : { kind: 'views', viewId: resourceId };
    case 'diff':
      return resourceId === null
        ? { kind: 'diff' }
        : { kind: 'diff', changesetId: resourceId };
    case 'git':
      return { kind: 'git' };
    case 'settings':
      return resourceId === null
        ? { kind: 'settings' }
        : { kind: 'settings', section: resourceId };
    case 'skills':
      return resourceId === null
        ? { kind: 'skills' }
        : { kind: 'skills', name: resourceId };
  }
}

function defaultLabelForRoute(
  destination: ActiveDestination,
  resourceId: string | null,
): string {
  if (destination === 'settings') return 'Settings';
  if (resourceId === null) {
    return destination.charAt(0).toUpperCase() + destination.slice(1);
  }
  if (destination === 'files') {
    const parts = resourceId.split('/');
    return parts[parts.length - 1] ?? resourceId;
  }
  // Chat session tabs default to "New conversation" until the chat slice
  // surfaces the real session title (resolved at render time via
  // `selectOpenTabsWithLabels`). Falling back to the raw session id —
  // e.g. `sess-pal82hfe-1779901267243` — is unreadable, so we never show
  // it as a tab label.
  if (destination === 'chat') return 'New conversation';
  return resourceId;
}

// ── Selectors ───────────────────────────────────────────────────────────

/** Returns `openTabs` sorted per `tabOrder` — the canonical render order. */
export function selectOpenTabs(state: NavigationSliceState): TabState[] {
  const byId = new Map(state.openTabs.map((t) => [t.id, t] as const));
  const ordered: TabState[] = [];
  for (const id of state.tabOrder) {
    const t = byId.get(id);
    if (t !== undefined) ordered.push(t);
  }
  // Append any tabs missing from tabOrder (defensive — should not happen).
  for (const t of state.openTabs) {
    if (!state.tabOrder.includes(t.id)) ordered.push(t);
  }
  return ordered;
}

/** Returns the `TabState` for `activeTabId` or `null`. */
export function selectActiveTab(state: NavigationSliceState): TabState | null {
  if (state.activeTabId === null) return null;
  return state.openTabs.find((t) => t.id === state.activeTabId) ?? null;
}

/** Returns whether a tab is pinned. */
export function selectIsTabPinned(tabId: string) {
  return (state: NavigationSliceState): boolean => {
    return state.openTabs.find((t) => t.id === tabId)?.pinned ?? false;
  };
}

export function selectSoftLimitNotice(state: NavigationSliceState): boolean {
  return state.softLimitNotice;
}
